import React, { useEffect, useRef } from 'react';
import CountrySelectionModal from '../CountrySelectionModal/CountrySelectionModal';
import IconToggleArrow from '@pano-project/assets/icons/IconToggleArrow';
import { getCountryFlagImage } from 'utils/helper-functions/getCountryFlagImage';
import { LazyImage } from '@pano-project/components/LazyImage/LazyImage';
import './PhoneInput.sass';

const PhoneInputComposite = ({
    name,
    codes,
    prefix,
    number,
    countryCode,
    isModalOpen,
    setIsModalOpen,
    onNumberChange,
    onPrefixChange,
    onFocus,
    onBlur,
    placeholder,
    disabled,
    required,
    langBack,
    s,
    autoFocus = false,
}) => {
    const numberInputRef = useRef(null);
    const compositeRef = useRef(null);
    const isInternalMouseDownRef = useRef(false);

    useEffect(() => {
        if (autoFocus && numberInputRef.current && !disabled) {
            const id = requestAnimationFrame(() => {
                numberInputRef.current?.focus();
            });
            return () => cancelAnimationFrame(id);
        }
    }, [autoFocus, disabled]);

    const handleCompositeMouseDown = () => {
        isInternalMouseDownRef.current = true;
        setTimeout(() => { isInternalMouseDownRef.current = false; }, 0);
    };

    const handleInternalBlur = (e) => {
        if (e.relatedTarget && compositeRef.current?.contains(e.relatedTarget)) return;

        if (isInternalMouseDownRef.current) {
            isInternalMouseDownRef.current = false;
            return;
        }

        onBlur && onBlur();
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        requestAnimationFrame(() => numberInputRef.current?.focus());
    };

    return (
        <div ref={compositeRef} onMouseDown={handleCompositeMouseDown} className={`phone-input-composite ${disabled ? 'disabled' : ''}`}>
            <button
                type="button"
                name="phone_prefix"
                className={`phone-input__trigger ${s ? s('input') : ''}`}
                onClick={() => !disabled && setIsModalOpen(true)}
                onFocus={() => onFocus && onFocus(name)}
                onBlur={handleInternalBlur}
                disabled={disabled}
                aria-label="Country dial code"
            >
                {getCountryFlagImage(countryCode) && (
                    <span className="phone-input__trigger-flag">
                        <LazyImage src={getCountryFlagImage(countryCode)} alt={countryCode} />
                    </span>
                )}
                <span className="phone-input__trigger-value">+{prefix}</span>
                <span className={`phone-input__trigger-arrow ${isModalOpen ? 'opened' : ''}`}>
                    <IconToggleArrow />
                </span>
            </button>
            <CountrySelectionModal
                mode="regionCode"
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onDialCodeChange={onPrefixChange}
                selectedCountryCode={countryCode}
                selectedValue={prefix}
                dialCodes={codes}
                langBack={langBack || ((k) => k)}
                hideFlagsInRegionCode={false}
            />
            <span className="phone-input__splitter" aria-hidden="true" />
            <input
                ref={numberInputRef}
                name={name}
                type="tel"
                value={number}
                onChange={onNumberChange}
                onFocus={() => onFocus && onFocus(name)}
                onBlur={handleInternalBlur}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                className={`${s ? s('input') : ''} phone-input__number`}
                inputMode="numeric"
                autoComplete="tel-national"
            />
        </div>
    );
};

export default PhoneInputComposite;
